import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Group, MultiSelect } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import { Save } from '@carbon/icons-react';

import styles from './SetTeamMemberTags.module.scss';

import { useAssignUserTagsMutation, useGetEntityByIdQuery } from 'api/entityEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import TeamMemberTags from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamMemberTags/TeamMemberTags';
import { useTeamMemberContext } from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/Details/TeamMemberDetailsContext';
import sharedMessages from 'Components/utils/sharedMessages';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { TagDto } from 'api/dtos/tag';

const mergeTagsById = (tags: TagDto[]) => Array.from(
    new Map(tags.map((tag) => [tag.id, tag])).values(),
);

const SetTeamMemberTags = () => {
    const intl = useIntl();
    const { values } = useTeamMemberContext();
    const { tags, teamMemberId } = values;
    const tagsIds = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);
    const currentEntityId = useGetCurrentEntityId();
    const { data: entity } = useGetEntityByIdQuery(currentEntityId ?? '', { skip: !currentEntityId });
    const [selectedItems, setSelectedItems] = useState(tags);
    const selectedIds = useMemo(() => selectedItems.map((item) => item.id), [selectedItems]);
    const allTags = useMemo(
        () => mergeTagsById([...(entity?.tags ?? []), ...tags]),
        [entity?.tags, tags],
    );
    const tagOptions = useMemo(() => allTags.map((tag) => ({
        value: tag.id,
        label: tag.name,
    })), [allTags]);
    const [assignTags, { isLoading }] = useAssignUserTagsMutation();
    const {
        isAdmin,
        isModerator,
        isOwner,
    } = useGetUserPermission();
    const canManageTags = isOwner || isAdmin || isModerator;

    const isDisabled = useMemo(() => tags.length === selectedItems.length && selectedItems.every((tag) => tagsIds.has(tag.id)), [selectedItems, tags.length, tagsIds]);

    useEffect(() => {
        setSelectedItems(tags);
    }, [tags]);

    const onChange = useCallback((selectedTagIds: string[]) => {
        setSelectedItems(allTags.filter((tag) => selectedTagIds.includes(tag.id)));
    }, [allTags]);

    const onSubmit = async () => {
        if (!currentEntityId) {
            return;
        }

        const tagIds = selectedItems.map((item) => item.id);
        await assignTags({
            tagIds,
            entityId: currentEntityId,
            userId: teamMemberId,
        }).unwrap();
    };

    return (
        <div>
            <Group align="flex-end" gap="md" wrap="nowrap" className={styles.innerContainer}>
                <MultiSelect
                    id="team-member-tag-picker"
                    label={intl.formatMessage(messages.title)}
                    data={tagOptions}
                    disabled={isLoading || !canManageTags}
                    value={selectedIds}
                    onChange={onChange}
                    searchable
                    clearable
                    hidePickedOptions={false}
                    className={styles.select}
                    comboboxProps={{ withinPortal: false, zIndex: 10000 }}
                />
                <Button
                    variant="outline"
                    leftSection={<Save size={16} />}
                    className={styles.saveButton}
                    disabled={(isDisabled || isLoading) || !canManageTags}
                    loading={isLoading}
                    onClick={onSubmit}
                >
                    {intl.formatMessage(sharedMessages.saveChanges)}
                </Button>
            </Group>
            <TeamMemberTags tags={selectedItems} />
        </div>
    );
};

const messages = defineMessages({
    setRoles: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.Details.SpecificTeamMemberView.SetTeamMemberTags.SetTeamMemberTags.setRoles',
        defaultMessage: 'Ustaw role użytkownika',
    },
    title: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.Details.SpecificTeamMemberView.SetTeamMemberTags.SetTeamMemberTags.title',
        defaultMessage: 'Role użytkownika',
    },
});

export default SetTeamMemberTags;
